import React, {useMemo} from 'react';

import {buildCatalogMap, buildCatalogTree} from '../lib/catalog';
import DateTimeInput from './DateTimeInput';
import Input from './Input';
import CatalogInput from './CatalogInput';

function ReportParameterInput({parameter, value, validationText, onChange, catalog}) {
  // Only catalog_question reads these. Memoized so typing in any parameter doesn't rebuild them from the same catalog.
  const usesCatalog = parameter.name === 'catalog_question';
  const catalogById = useMemo(() => usesCatalog ? buildCatalogMap(catalog) : {}, [usesCatalog, catalog]);
  const catalogTree = useMemo(() => usesCatalog ? buildCatalogTree(catalog) : [], [usesCatalog, catalog]);

  switch (true) {
    case parameter.name === 'date':
    case parameter.name === 'start_date':
    case parameter.name === 'end_date':
    case parameter.dataType === 7:
    case parameter.dataType === 4:
      return (
        <DateTimeInput
          id={`parameter_${parameter.name}`}
          label={parameter.label}
          helpText={parameter.helpText}
          date={value}
          onChange={value => onChange(parameter, value)}
          invalid={validationText}
          validationText={validationText}
        />
      );

    case parameter.name === 'catalog_item':
      return (
        <CatalogInput
          catalog={catalog}
          label={parameter.label}
          value={value}
          onChange={value => onChange(parameter, value)}
          invalid={validationText}
          validationText={validationText}
        />
      );

    case parameter.name === 'catalog_question':
      value = resolveCatalogFromPath(value, catalogTree);

      if (value) {
        value = value.id;
      }

      return (
        <CatalogInput
          catalog={catalog}
          label={parameter.label}
          value={value}
          onChange={value => onChange(parameter, convertCatalogIdToPath(value, catalogById))}
          invalid={validationText}
          validationText={validationText}
        />
      );
    
    case parameter.choices && parameter.choices.length > 0:
      const hasDefault = parameter.choices.map(choice => choice.value).indexOf(parameter.defaultValue) > -1;
      return (
        <Input
          id={`parameter_${parameter.name}`}
          type="select"
          label={parameter.label}
          helpText={parameter.helpText}
          value={value || ''}
          onChange={e => onChange(parameter, e.target.value)}
          invalid={validationText}
          validationText={validationText}>
          <option value="">Choose an option...</option>
          {parameter.defaultValue && !hasDefault && (<option key={parameter.defaultValue} value={parameter.defaultValue}>{parameter.defaultValue}</option>)}
          {parameter.choices.map(choice => (
            <option key={choice.value} value={choice.value}>{choice.label}</option>
          ))}
        </Input>
      );

    default:
      return (
        <Input
          id={`parameter_${parameter.name}`}
          type="text"
          label={parameter.label}
          helpText={parameter.helpText}
          value={value || ''}
          onChange={e => onChange(parameter, e.target.value)}
          invalid={validationText}
          validationText={validationText}
        />
      );
  }
}

function resolveCatalogFromPath(path, catalogTree) {
  if (!path) {
    return null;
  }

  const parts = path.split('->');
  let item;
  
  for (let i = 0; i < parts.length; i++) {
    item = catalogTree.filter(item => item.name === parts[i])[0];

    if (!item) {
      return null;
    }

    catalogTree = item.children;
  }

  return item;
}

function convertCatalogIdToPath(id, catalogById) {
  let item = catalogById[id];
  const path = [item];

  while (item.parentId) {
    item = catalogById[item.parentId];
    path.push(item);
  }

  return path.reverse().map(item => item.name).join(' → ');
}

export default ReportParameterInput;